import { setAnalyticsEvent } from '../../analytics/state'
import { Prescription, PrescriptionAction, PrescriptionProduct } from './types'

const addProductToPrescription = (
  newProduct: PrescriptionProduct,
  currentPrescription: ReadonlyArray<PrescriptionProduct>,
): ReadonlyArray<PrescriptionProduct> => {
  if (!currentPrescription.find((product) => product.sku === newProduct.sku)) {
    return [...currentPrescription, newProduct]
  }
  return currentPrescription.map((product) =>
    product.sku === newProduct.sku
      ? {
          ...product,
          quantity: product.quantity + newProduct.quantity,
          autoShip: newProduct.autoShip,
        }
      : product,
  )
}

// behavior is the same as REMOVE then ADD (but we keep the same order if the SKU is in the prescription)
const updateProduct = (
  productToUpdateSku: string,
  newProduct: PrescriptionProduct,
  currentPrescription: ReadonlyArray<PrescriptionProduct>,
): ReadonlyArray<PrescriptionProduct> => {
  const productToUpdateIndex = currentPrescription.findIndex((p) => p.sku === productToUpdateSku)
  const newProductIndex = currentPrescription.findIndex((p) => p.sku === newProduct.sku)

  const isProductToUpdateInPrescription = productToUpdateIndex !== -1
  const isNewProductInPrescription = newProductIndex !== -1

  if (!isProductToUpdateInPrescription) {
    return addProductToPrescription(newProduct, currentPrescription)
  }

  if (isProductToUpdateInPrescription && !isNewProductInPrescription) {
    return currentPrescription.map((p, index) => (index === productToUpdateIndex ? newProduct : p))
  }

  // now we know that both products are in the prescription
  return productToUpdateSku !== newProduct.sku
    ? addProductToPrescription(
        newProduct,
        currentPrescription.filter((p) => p.sku !== productToUpdateSku),
      )
    : currentPrescription.map((p, index) => (index === productToUpdateIndex ? newProduct : p))
}

export const prescriptionBuilderReducer = (
  state: Prescription,
  action: PrescriptionAction,
): Prescription => {
  switch (action.type) {
    case 'add': {
      const newState = {
        ...state,
        products: addProductToPrescription(action.product, state.products),
      }

      // Fire analytics event if product was actually added (not just quantity updated)
      const wasProductAdded = !state.products.find((product) => product.sku === action.product.sku)
      if (wasProductAdded && action.source) {
        setAnalyticsEvent({
          event: 'add_to_escript',
          source: action.source,
          item_sku: action.product.sku,
          item_urlKey: action.product.urlKey,
        })
      }

      return newState
    }
    case 'addMany': {
      const addManyState = {
        ...state,
        products: action.products.reduce(
          (acc, product) => addProductToPrescription(product, acc),
          state.products,
        ),
      }

      // Fire analytics for each newly added product
      if (action.source) {
        action.products.forEach((product) => {
          const wasProductAdded = !state.products.find((p) => p.sku === product.sku)
          if (wasProductAdded && action.source) {
            setAnalyticsEvent({
              event: 'add_to_escript',
              source: action.source,
              item_sku: product.sku,
              item_urlKey: product.urlKey,
            })
          }
        })
      }

      return addManyState
    }
    case 'remove':
      // if the product is not in the prescription, don't update the state
      if (!state.products.find((product) => product.sku === action.sku)) {
        return state
      }
      return { ...state, products: state.products.filter((product) => product.sku !== action.sku) }
    case 'setPromoCode':
      // if the promoCode is the same, don't update the state
      if (action.promoCode === state.promoCode) {
        return state
      }
      return { ...state, promoCode: action.promoCode }
    case 'updateQuantity':
      // if the quantity is the same, don't update the state
      if (action.quantity === state.products.find((p) => p.sku === action.sku)?.quantity) {
        return state
      }
      return {
        ...state,
        products: state.products.map((product) =>
          product.sku === action.sku
            ? { ...product, quantity: action.quantity > 0 ? action.quantity : 1 }
            : product,
        ),
      }
    case 'updateAutoShip':
      // if the autoShip is the same, don't update the state
      if (action.autoShip === state.products.find((p) => p.sku === action.sku)?.autoShip) {
        return state
      }
      return {
        ...state,
        products: state.products.map((product) =>
          product.sku === action.sku ? { ...product, autoShip: action.autoShip } : product,
        ),
      }
    case 'updateProduct':
      return {
        ...state,
        products: updateProduct(action.sku, action.product, state.products),
      }
    case 'clearAll':
      return {
        ...state,
        products: [],
      }
    case 'set':
      return {
        ...state,
        ...action.prescription,
      }
  }
}
